import { useState } from 'react'
import {
    Alert,
    Box,
    Chip,
    Divider,
    IconButton,
    Link,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material'
import BlockIcon from '@mui/icons-material/Block'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import ReplayIcon from '@mui/icons-material/Replay'
import dayjs from 'dayjs'
import humanizeDuration from 'humanize-duration'
import { enqueueSnackbar } from 'notistack'
import { Link as WouterLink } from 'wouter'
import DashboardLayout from '@pages/Dashboard/components/DashboardLayout'
import { copyValue } from '@utils'
import API from '@enums/api'
import { PodiumDetail, PodiumPeripheral } from '@types'
import PodiumActionDialog, {
    PodiumAction,
} from '../components/PodiumActionDialog'
import usePodiumPage from './usePodiumPage'
import PodiumJobsCard from './PodiumJobsCard'
import PodiumEventsCard from './PodiumEventsCard'

/** Below this the device refuses to start recording, so it predicts the next ticket. */
const LOW_DISK_PERCENT = 10

/** Device-authored lowercase states ('authorized', 'denied') read better sentence-cased. */
function sentenceCase(value: string | null): string {
    if (!value) return '—'
    return value.charAt(0).toUpperCase() + value.slice(1)
}

function formatUptime(seconds: number | null): string {
    if (typeof seconds !== 'number') return '—'
    return humanizeDuration(seconds * 1000, {
        largest: 2,
        round: true,
        units: ['d', 'h', 'm'],
    })
}

/**
 * "4 minutes ago" for a timestamp. Built on humanize-duration rather than dayjs's
 * `fromNow`, because this repo never extends dayjs with the relativeTime plugin — calling
 * `fromNow()` compiles but throws at runtime.
 */
function formatAge(iso: string | null): string {
    if (!iso) return '—'
    const ms = Date.now() - new Date(iso).getTime()
    if (!Number.isFinite(ms)) return '—'
    if (ms < 60_000) return 'just now'
    return `${humanizeDuration(ms, {
        largest: 1,
        round: true,
        units: ['d', 'h', 'm'],
    })} ago`
}

function formatMemory(bytes?: number): string {
    if (typeof bytes !== 'number' || bytes <= 0) return '—'
    return `${Math.round(bytes / 1024 ** 3)} GB`
}

/** One labelled figure in the health strip. */
function Tile({
    label,
    value,
    tone,
}: {
    label: string
    value: string
    tone?: 'error' | 'warning' | 'success'
}) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                minWidth: 120,
                flexGrow: 1,
                borderColor: tone ? `${tone}.main` : undefined,
            }}
        >
            <Typography variant="overline" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="h6" color={tone ? `${tone}.main` : undefined}>
                {value}
            </Typography>
        </Paper>
    )
}

function Field({
    label,
    value,
    tone,
}: {
    label: string
    value: string
    tone?: 'error' | 'warning'
}) {
    return (
        <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
            <Typography
                variant="body2"
                color={tone ? `${tone}.main` : undefined}
                sx={{ textAlign: 'right' }}
            >
                {value}
            </Typography>
        </Stack>
    )
}

function PeripheralRow({ device }: { device: PodiumPeripheral }) {
    const detail = [device.connection, device.maxResolution]
        .filter(Boolean)
        .join(' · ')
    return (
        <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
        >
            <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2">{device.name || '—'}</Typography>
                {device.isActive && (
                    <Chip
                        label="active"
                        size="small"
                        color="success"
                        variant="outlined"
                    />
                )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
                {detail || '—'}
            </Typography>
        </Stack>
    )
}

/**
 * Everything known about one Podium, so the fleet table doesn't have to keep growing a
 * column per fact. Device state and hardware come from the detail endpoint; the jobs and
 * events cards fetch and page independently.
 */
export default function PodiumPage() {
    const { hardwareId, podium, loading, error, reload } = usePodiumPage()
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
    const [dialogAction, setDialogAction] = useState<PodiumAction | null>(null)

    if (loading) return <DashboardLayout loading />

    if (error || !podium || !hardwareId) {
        return (
            <DashboardLayout>
                <Alert severity="error">
                    {error ?? 'This podium could not be found.'}
                </Alert>
            </DashboardLayout>
        )
    }

    const retryAllFailedJobs = async () => {
        const res = await API.ha.podium.retryJobs(hardwareId).catch(() => null)
        enqueueSnackbar(
            res?.ok
                ? {
                      variant: 'success',
                      message:
                          'Retry sent. The device reports progress as it works through its failed jobs.',
                  }
                : {
                      variant: 'error',
                      message:
                          'Retry was not delivered — the device may be unreachable.',
                  }
        )
        void reload()
    }

    const retired = Boolean(
        podium.decommissioned_at ?? podium.decommission_requested_at
    )
    const lowDisk =
        typeof podium.disk_free_percent === 'number' &&
        podium.disk_free_percent < LOW_DISK_PERCENT
    const failedJobs = podium.failed_job_count ?? 0

    const isDisabled =
        podium.status === 'disabled' ||
        podium.desired_admin_state === 'disabled'

    return (
        <DashboardLayout breadcrumbLabel={podium.name || podium.hardware_id}>
            <Stack spacing={3}>
                {/* Identity + the actions that act on the whole device. */}
                <Paper sx={{ p: 3 }}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        spacing={2}
                    >
                        <Box>
                            <Typography variant="h5">
                                {podium.name || 'Unnamed podium'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {podium.description || 'No location set'}
                                {podium.org_id && podium.org_name ? (
                                    <>
                                        {' · '}
                                        <Link
                                            component={WouterLink}
                                            href={`/organizations/${podium.org_id}`}
                                            underline="hover"
                                        >
                                            {podium.org_name}
                                        </Link>
                                    </>
                                ) : (
                                    ''
                                )}
                            </Typography>
                            <Stack
                                direction="row"
                                spacing={0.5}
                                alignItems="center"
                                sx={{ mt: 1 }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{ fontFamily: 'monospace' }}
                                >
                                    {podium.hardware_id ?? '—'}
                                </Typography>
                                {podium.hardware_id && (
                                    <Tooltip title="Copy device id">
                                        <IconButton
                                            size="small"
                                            // copyValue RETURNS the handler — calling it
                                            // inside an arrow just builds one and drops it.
                                            onClick={copyValue(
                                                podium.hardware_id ?? ''
                                            )}
                                        >
                                            <ContentCopyIcon fontSize="inherit" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Stack>
                        </Box>

                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="flex-start"
                        >
                            <Chip
                                label={podium.status}
                                size="small"
                                color={
                                    podium.status === 'recording'
                                        ? 'error'
                                        : 'default'
                                }
                                variant="outlined"
                            />
                            <Chip
                                label={podium.is_online ? 'online' : 'offline'}
                                size="small"
                                color={podium.is_online ? 'success' : 'default'}
                                variant="outlined"
                            />
                            {retired && (
                                <Chip
                                    label="decommissioned"
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                            {/* A wiped device has no IoT identity left, so commands can
                                never reach it — the server refuses them with a 409. */}
                            {!retired && (
                                <Tooltip title="Device actions">
                                    <IconButton
                                        size="small"
                                        onClick={(e) =>
                                            setMenuAnchor(e.currentTarget)
                                        }
                                    >
                                        <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Stack>
                    </Stack>
                </Paper>

                <Menu
                    anchorEl={menuAnchor}
                    open={menuAnchor !== null}
                    onClose={() => setMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    {/* Retrying is safe and reversible — the device refuses jobs it can't
                        act on — so it goes straight through without a confirmation. */}
                    <MenuItem
                        onClick={() => {
                            setMenuAnchor(null)
                            void retryAllFailedJobs()
                        }}
                    >
                        <ListItemIcon>
                            <ReplayIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Retry all failed jobs</ListItemText>
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            setMenuAnchor(null)
                            setDialogAction(isDisabled ? 'enable' : 'disable')
                        }}
                    >
                        <ListItemIcon>
                            {isDisabled ? (
                                <CheckCircleIcon fontSize="small" />
                            ) : (
                                <BlockIcon fontSize="small" />
                            )}
                        </ListItemIcon>
                        <ListItemText>
                            {isDisabled ? 'Enable' : 'Disable'}
                        </ListItemText>
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            setMenuAnchor(null)
                            setDialogAction('wipe')
                        }}
                    >
                        <ListItemIcon>
                            <DeleteForeverIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Wipe</ListItemText>
                    </MenuItem>
                </Menu>

                <PodiumActionDialog
                    action={dialogAction}
                    podium={podium}
                    onClose={() => setDialogAction(null)}
                    onConfirmed={() => {
                        setDialogAction(null)
                        void reload()
                    }}
                />

                {(failedJobs > 0 || lowDisk) && (
                    <Alert severity="warning">
                        {failedJobs > 0 &&
                            `${failedJobs} failed job${failedJobs === 1 ? '' : 's'}.`}
                        {failedJobs > 0 && lowDisk ? ' ' : ''}
                        {lowDisk &&
                            'Disk space is low — the device refuses to start recording below its floor.'}
                    </Alert>
                )}

                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <Tile
                        label="Disk free"
                        value={
                            typeof podium.disk_free_percent === 'number'
                                ? `${Math.round(podium.disk_free_percent)}%`
                                : '—'
                        }
                        tone={lowDisk ? 'error' : undefined}
                    />
                    <Tile
                        label="Uptime"
                        value={formatUptime(podium.uptime_seconds)}
                    />
                    <Tile label="Version" value={podium.app_version ?? '—'} />
                    <Tile
                        label="Failed jobs"
                        value={String(failedJobs)}
                        tone={failedJobs > 0 ? 'warning' : undefined}
                    />
                    <Tile
                        label="Heartbeat"
                        value={formatAge(podium.last_health_at)}
                    />
                    <Tile
                        label="Camera"
                        value={podium.camera_permission ?? '—'}
                        tone={
                            podium.camera_permission === 'authorized'
                                ? 'success'
                                : podium.camera_permission
                                  ? 'error'
                                  : undefined
                        }
                    />
                    <Tile
                        label="Microphone"
                        value={podium.microphone_permission ?? '—'}
                        tone={
                            podium.microphone_permission === 'authorized'
                                ? 'success'
                                : podium.microphone_permission
                                  ? 'error'
                                  : undefined
                        }
                    />
                </Stack>

                <Stack
                    direction={{ xs: 'column', lg: 'row' }}
                    spacing={3}
                    alignItems="flex-start"
                >
                    <Stack
                        spacing={3}
                        sx={{ width: { xs: '100%', lg: 340 }, flexShrink: 0 }}
                    >
                        <DeviceCard podium={podium} />
                        <PeripheralsCard podium={podium} />
                        <ModelsCard podium={podium} />
                        <UpdaterCard podium={podium} />
                        {podium.recording_started_at && (
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    Now recording
                                </Typography>
                                <Typography variant="body2">
                                    Started{' '}
                                    {dayjs(podium.recording_started_at).format(
                                        'HH:mm'
                                    )}{' '}
                                    ({formatAge(podium.recording_started_at)})
                                </Typography>
                            </Paper>
                        )}
                    </Stack>

                    <Stack
                        spacing={3}
                        sx={{ flexGrow: 1, minWidth: 0, width: '100%' }}
                    >
                        <PodiumJobsCard
                            hardwareId={hardwareId}
                            retired={retired}
                            onRetried={reload}
                        />
                        <PodiumEventsCard hardwareId={hardwareId} />
                    </Stack>
                </Stack>
            </Stack>
        </DashboardLayout>
    )
}

/**
 * A staff command the device hasn't caught up with yet, or null when there's nothing
 * outstanding.
 *
 * `desired_admin_state` is intent, not observation: Habitat Admin records it immediately
 * and re-fires the command on every reconnect until the device applies it. Once applied
 * it agrees with the device's own state and stops being worth saying — so this reports
 * only the gap between the two, which is the thing someone would act on.
 */
function pendingAdminState(podium: PodiumDetail): string | null {
    const desired = podium.desired_admin_state
    if (!desired) return null

    // A wipe is done when the device confirms it, which is what sets the tombstone —
    // not when its status changes.
    if (desired === 'wipe') {
        return podium.decommissioned_at ? null : 'Wipe'
    }
    if (desired === 'disabled') {
        return podium.status === 'disabled' ? null : 'Disable'
    }
    // 'active' is the enable marker, and Habitat Admin clears it to null once the device
    // reports a healthy status — so it only means anything while it still reads disabled.
    return podium.status === 'disabled' ? 'Enable' : null
}

function DeviceCard({ podium }: { podium: PodiumDetail }) {
    const hw = podium.hardware
    const pending = pendingAdminState(podium)
    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
                Device
            </Typography>
            <Stack spacing={1} divider={<Divider flexItem />}>
                <Field
                    label="Model"
                    value={hw?.marketingName || hw?.modelIdentifier || '—'}
                />
                <Field label="Chip" value={hw?.chip || '—'} />
                <Field label="Memory" value={formatMemory(hw?.memoryBytes)} />
                <Field label="Serial" value={hw?.serialNumber || '—'} />
                <Field
                    label="macOS"
                    value={
                        hw?.osVersion
                            ? `${hw.osVersion}${hw.osBuild ? ` (${hw.osBuild})` : ''}`
                            : '—'
                    }
                />
                <Field
                    label="Onboarded"
                    value={dayjs(podium.created_at).format('D MMM YYYY')}
                />
                {pending && (
                    <Field
                        label="Requested state"
                        value={pending}
                        tone="warning"
                    />
                )}
            </Stack>
        </Paper>
    )
}

function PeripheralsCard({ podium }: { podium: PodiumDetail }) {
    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
                Peripherals
            </Typography>

            <Typography variant="overline" color="text.secondary">
                Cameras
            </Typography>
            <Stack spacing={1} sx={{ mb: 2 }}>
                {podium.cameras.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        None reported
                    </Typography>
                )}
                {podium.cameras.map((camera, index) => (
                    <PeripheralRow
                        key={camera.id ?? `camera-${index}`}
                        device={camera}
                    />
                ))}
            </Stack>

            <Typography variant="overline" color="text.secondary">
                Microphones
            </Typography>
            <Stack spacing={1}>
                {podium.microphones.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        None reported
                    </Typography>
                )}
                {podium.microphones.map((mic, index) => (
                    <PeripheralRow
                        key={mic.id ?? `mic-${index}`}
                        device={mic}
                    />
                ))}
            </Stack>
        </Paper>
    )
}

/** A model's status, plus its error when it has one. */
function ModelRow({
    label,
    status,
    error,
    suffix,
}: {
    label: string
    status: string | null
    error: string | null
    suffix?: string
}) {
    const failed = status === 'failed'
    return (
        <Box>
            <Field
                label={label}
                value={`${sentenceCase(status)}${suffix ?? ''}`}
                tone={failed ? 'error' : undefined}
            />
            {error && (
                // Device-authored: the reason a model is unusable, which decides whether
                // this is a disk, a firewall or a bad download URL.
                <Typography
                    variant="caption"
                    color="error.main"
                    sx={{
                        display: 'block',
                        mt: 0.25,
                        whiteSpace: 'pre-wrap',
                        overflowWrap: 'anywhere',
                    }}
                >
                    {error}
                </Typography>
            )}
        </Box>
    )
}

/**
 * Readiness of the three on-device models, from the last heartbeat.
 *
 * Recording works without them; post-processing does not. A station whose summary model
 * never finished downloading records lectures all week and produces no notes, and this
 * card is where that shows up before anyone reads the job failures.
 */
function ModelsCard({ podium }: { podium: PodiumDetail }) {
    const models = podium.models

    if (!models) {
        return (
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Models
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    No model report yet. Devices running an older app version do
                    not send one.
                </Typography>
            </Paper>
        )
    }

    const version =
        models.summarizationVersion !== null
            ? ` (v${models.summarizationVersion})`
            : ''

    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
                Models
            </Typography>

            <Stack spacing={1} divider={<Divider flexItem />}>
                <ModelRow
                    label="Transcription"
                    status={models.transcription}
                    error={models.transcriptionError}
                />
                <ModelRow
                    label="Diarization"
                    status={models.diarization}
                    error={models.diarizationError}
                />
                <ModelRow
                    label="Summarization"
                    status={models.summarization}
                    error={models.summarizationError}
                    suffix={version}
                />
            </Stack>

            {models.summarizationUpToDate === false && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    A newer summary model ships with this app version; the
                    device is still on the previous one.
                </Alert>
            )}
        </Paper>
    )
}

/**
 * Where this device is in the auto-update cycle, from the last heartbeat.
 *
 * The question it answers is "why is this station still on the old version" — so the
 * fields that explain a stall (blocked reason, deployment issue, last error) are given
 * room rather than being squeezed into a label/value row, and the staged version's age is
 * spelled out, since a rollout that has sat staged for days is the thing worth chasing.
 *
 * All of it is as fresh as the heartbeat, whose age the Heartbeat tile above already
 * gives. There is deliberately no last-check time: the device reports its update
 * posture, not Sparkle's poll history.
 */
function UpdaterCard({ podium }: { podium: PodiumDetail }) {
    const update = podium.update
    const version = podium.app_version
        ? `${podium.app_version}${podium.app_build ? ` (build ${podium.app_build})` : ''}`
        : '—'

    const staged = update?.stagedVersion
    const stagedAt = update?.stagedAt ? dayjs(update.stagedAt) : null

    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
                Updater
            </Typography>

            <Stack spacing={1} divider={<Divider flexItem />}>
                <Field label="Running" value={version} />
                <Field
                    label="Status"
                    value={sentenceCase(update?.status ?? null)}
                />
                <Field
                    label="Update waiting"
                    value={staged ? staged : update ? 'None' : '—'}
                />
                {stagedAt && (
                    <Field
                        label="Staged"
                        value={`${stagedAt.format('D MMM HH:mm')} (${formatAge(update?.stagedAt ?? null)})`}
                    />
                )}
                <Field
                    label="Self-relaunch"
                    value={
                        podium.launch_at_login === null
                            ? '—'
                            : podium.launch_at_login
                              ? 'Enabled'
                              : 'Disabled'
                    }
                />
            </Stack>

            {!update && (
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 2 }}
                >
                    No updater report yet. Devices running an older app version
                    do not send one.
                </Typography>
            )}

            {update?.deploymentIssue && (
                // The one line here that is a call to action: silent updates cannot work
                // at all until someone fixes this on the machine.
                <Alert severity="error" sx={{ mt: 2 }}>
                    This device cannot self-update: {update.deploymentIssue}
                </Alert>
            )}

            {update?.blockedReason && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    Install deferred: {update.blockedReason}
                </Alert>
            )}

            {update?.lastError && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    {update.lastError}
                </Alert>
            )}
        </Paper>
    )
}
